/**
 *  @class
 *  @function CollectionTabs
 */
if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this._onResize = null;
      this._onBlockSelect = null;
      this._fetchCache = new Map();
      this._fetchPromises = new Map();
      this._cardResizeObserver = null;
      this._cardResizeFrame = null;
      this._cardHeights = new WeakMap();
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        this.buttons = Array.from(this.querySelectorAll('button'));
        this.buttons.forEach((button) => button.setAttribute('aria-pressed', button.classList.contains('active') ? 'true' : 'false'));
        let sectionHeader = this.closest('.section-header');
        this.links = sectionHeader ? Array.from(sectionHeader.querySelectorAll('.linked-to-tab')) : [];
        const destinations = this.closest('.collection-tabs')?.querySelectorAll('.mutuki-tab-destinations .linked-to-tab');
        if (destinations?.length) this.links = Array.from(destinations);
        this.target = this.dataset.target;
        this.slider = document.getElementById(this.target);

        if (!this.slider) return;

        this.productLimit = parseInt(this.slider.dataset.productLimit, 10) || 6;
        this.columnsDesktop = parseInt(this.slider.dataset.columnsDesktop, 10) || 4;
        this.columnsClass = this.getColumnsClass(this.columnsDesktop);

        // Cache items grouped by collection handle
        this.collectionMap = new Map();
        this.allItems = this.slider.querySelectorAll('[data-collection]');
        this.allItems.forEach((item) => {
          let handle = item.dataset.collection;
          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }
          this.collectionMap.get(handle).push(item);
        });
        this.observeCardSizes();

        // Event delegation instead of per-button listeners
        this.addEventListener('click', (event) => {
          let button = event.target.closest('button');
          if (!button) return;
          event.preventDefault();
          let index = this.buttons.indexOf(button);
          if (index === -1) return;
          this.toggleButtons(index);
          this.toggleLinks(index);
          if (button.dataset.collection) {
            this.handleTabClick(button);
          }
        });

        // Debounce resize to avoid excessive Flickity recalculations
        this._onResize = debounce(() => {
          let flkty = this.getFlickity();
          if (flkty) {
            flkty.resize();
          }
        }, 150);
        window.addEventListener('resize', this._onResize);

        if (Shopify.designMode) {
          this._onBlockSelect = (event) => {
            const index = this.buttons.indexOf(event.target);
            if (index !== -1) {
              this.buttons[index].dispatchEvent(new Event('click'));
            }
          };
          this.addEventListener('shopify:block:select', this._onBlockSelect);
        }

        // Lazy-load inactive tabs after page load
        if (!Shopify.designMode) {
          this.prefetchInactiveTabs();
        }
      });
    }

    disconnectedCallback() {
      this._cardResizeObserver?.disconnect();
      this._cardResizeObserver = null;
      if (this._cardResizeFrame !== null) {
        cancelAnimationFrame(this._cardResizeFrame);
        this._cardResizeFrame = null;
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
      }
      if (this._onBlockSelect) {
        this.removeEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    getFlickity() {
      if (!this.slider) return null;
      return Flickity.data(this.slider) || null;
    }

    observeCardSizes() {
      if (typeof ResizeObserver === 'undefined') return;
      if (!this._cardResizeObserver) {
        this._cardResizeObserver = new ResizeObserver((entries) => {
          let changed = false;
          entries.forEach((entry) => {
            const height = entry.contentRect.height;
            const previous = this._cardHeights.get(entry.target);
            this._cardHeights.set(entry.target, height);
            if (height > 0 && height !== previous && entry.target.closest('[data-collection]')?.classList.contains('carousel__slide')) changed = true;
          });
          if (!changed || this._cardResizeFrame !== null) return;
          // Reviews can arrive after Flickity has measured the product cards.
          this._cardResizeFrame = requestAnimationFrame(() => {
            this._cardResizeFrame = null;
            this.getFlickity()?.resize();
          });
        });
      }
      this.slider.querySelectorAll('product-card').forEach((card) => this._cardResizeObserver.observe(card));
    }

    toggleButtons(index) {
      this.buttons.forEach((button) => { button.classList.remove('active'); button.setAttribute('aria-pressed', 'false'); });
      this.buttons[index].classList.add('active');
      this.buttons[index].setAttribute('aria-pressed', 'true');
    }

    toggleLinks(index) {
      if (this.links.length && this.links[index]) {
        this.links.forEach((link) => link.classList.remove('active'));
        this.links[index].classList.add('active');
      }
    }

    getColumnsClass(columns) {
      switch (columns) {
        case 2: return 'medium-6';
        case 3: return 'medium-4';
        case 4: return 'medium-6 large-3';
        case 5: return 'small-6 medium-4 large-1/5';
        default: return 'medium-6 large-4';
      }
    }

    toggleCollection(handle) {
      if (!this.slider) return;
      let flkty = this.getFlickity();
      let activeItems = this.collectionMap.get(handle);

      // Remove carousel__slide from all, add to active — single pass each
      this.allItems.forEach((item) => item.classList.remove('carousel__slide'));
      if (activeItems) {
        activeItems.forEach((item) => item.classList.add('carousel__slide'));
      }

      if (flkty) {
        // Initially inactive items may remain outside Flickity's slider.
        // Move them into the carousel before rebuilding the active cells.
        this.allItems.forEach((item) => {
          if (item.parentElement !== flkty.slider) {
            flkty.slider.appendChild(item);
          }
        });
        flkty.reloadCells();
        flkty.select(0, false, true);
        flkty.resize();
        flkty.dispatchEvent('scroll', null, [0]);
      }
    }

    handleTabClick(button) {
      let handle = button.dataset.collection;
      // Inert product templates defer image loading until this tab is opened.
      const deferred = Array.from(this.slider.querySelectorAll('template[data-deferred-tab]')).find((template) => template.dataset.deferredTab === handle);
      if (deferred) {
        const fragment = deferred.content.cloneNode(true);
        const items = Array.from(fragment.querySelectorAll('[data-collection]'));
        this.slider.appendChild(fragment);
        deferred.remove();
        this.collectionMap.set(handle, items);
        this.allItems = this.slider.querySelectorAll('[data-collection]');
        this.observeCardSizes();
      }
      if (this.collectionMap.has(handle)) {
        this.toggleCollection(handle);
        return;
      }
      // Products not loaded yet — fetch on demand
      let url = button.dataset.collectionUrl;
      if (!url) {
        this.toggleCollection(handle);
        return;
      }
      this.fetchCollection(handle, url).then(() => {
        this.toggleCollection(handle);
      });
    }

    prefetchInactiveTabs() {
      let loadTabs = () => {
        this.buttons.forEach((button, index) => {
          if (index === 0) return;
          let handle = button.dataset.collection;
          let url = button.dataset.collectionUrl;
          if (!handle || !url || this.collectionMap.has(handle)) return;
          this.fetchCollection(handle, url);
        });
      };

      if (document.readyState === 'complete') {
        loadTabs();
      } else {
        window.addEventListener('load', loadTabs, { once: true });
      }
    }

    fetchCollection(handle, collectionUrl) {
      if (this._fetchCache.has(handle)) {
        return Promise.resolve();
      }
      if (this._fetchPromises.has(handle)) {
        return this._fetchPromises.get(handle);
      }

      let fetchUrl = collectionUrl + '?section_id=collection-tabs-ajax';
      let promise = fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          let parser = new DOMParser();
          let doc = parser.parseFromString(html, 'text/html');
          let items = Array.from(doc.querySelectorAll('[data-collection]')).slice(0, this.productLimit);

          // Insert into the Flickity slider container (or slider root if Flickity not initialized)
          let flickitySlider = this.slider.querySelector('.flickity-slider');
          let container = flickitySlider || this.slider;
          items.forEach((item) => {
            this.columnsClass.split(' ').forEach((cls) => item.classList.add(cls));
            container.appendChild(item);
          });

          // Initialize lazy-loaded images
          container.querySelectorAll('.lazyload').forEach((img) => {
            lazySizes.loader.unveil(img);
          });

          // Update collectionMap and allItems
          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }
          items.forEach((item) => {
            this.collectionMap.get(handle).push(item);
          });
          this.allItems = this.slider.querySelectorAll('[data-collection]');
          this.observeCardSizes();
          this._fetchCache.set(handle, true);
          this._fetchPromises.delete(handle);
        })
        .catch(() => {
          this._fetchPromises.delete(handle);
        });

      this._fetchPromises.set(handle, promise);
      return promise;
    }
  }
  customElements.define('collection-tabs', CollectionTabs);
}
